import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { destroyGroupPayerNetworkStatuses } from 'services/api/groupPayerNetworkStatuses';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

import { countOf } from './facilityNetworkStatusColumns';

export type RemoveAllMappingsTarget = { kind: 'facility' | 'payer'; id: string; name: string; count: number };

type RemoveAllMappingsModalProps = {
  target: RemoveAllMappingsTarget;
  onClose: () => void;
};

export default function RemoveAllMappingsModal({ target, onClose }: RemoveAllMappingsModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();
  // `onSuccess` on the hook, not per-call: React Query drops per-call callbacks if the header ×
  // unmounts the modal mid-request, which would leave the table showing the old counts.
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyGroupPayerNetworkStatuses,
    errorToast: validationErrorMessage,
    onSuccess: () => {
      invalidateQueries({ groupPayerNetworkStatuses: true });
      addToast({ text: `All mappings removed for ${target.name}` });
      onClose();
    },
  });

  const isFacility = target.kind === 'facility';

  const handleRemove = () => mutate(isFacility ? { groupId: target.id } : { stediPayerId: target.id });

  return (
    <StandardModal title='Remove All Mappings?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{`${isFacility ? 'Facility' : 'Payer'}: ${target.name}`}</StandardModal.RecordName>
      <Content>
        {isFacility
          ? `Confirming this action will remove mapping to ${countOf(target.count, 'payer', 'payers')} for this facility. Are you sure to continue?`
          : `Confirming this action will remove this payer and its mapping to ${countOf(target.count, 'facility', 'facilities')}. Are you sure to continue?`}
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleRemove} loading={isPending} disabled={isPending}>
          Remove
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
